import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import './AdsManagement.css';

const AdsManagement = () => {
  const [sections, setSections] = useState([
    { section_number: 1, section_type: 'video', youtube_url: '', title: 'Section 1', is_active: true },
    { section_number: 2, section_type: 'logo', logo_url: '', title: 'Section 2 - Logo', is_active: true },
    { section_number: 3, section_type: 'video', youtube_url: '', title: 'Section 3', is_active: true }
  ]);
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');

  useEffect(() => {
    fetchAdsData();
  }, []);

  const fetchAdsData = async () => {
    try {
      const response = await axios.get(
        `/cashier/ads-sections`,
        getHeaders()
      );
      // API returns array directly
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        setSections(response.data);
      }

      // Fetch company logo
      const logoResponse = await axios.get(
        `/cashier/company-logo`,
        getHeaders()
      );
      setCompanyLogo(logoResponse.data.logo_url || '');
    } catch (error) {
      console.error('Error fetching ads data:', error);
      toast.error('Failed to load ads data');
    }
  };

  const extractYouTubeVideoId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  const handleSectionChange = (sectionNumber, field, value) => {
    setSections(prevSections =>
      prevSections.map(section =>
        section.section_number === sectionNumber
          ? { ...section, [field]: value }
          : section
      )
    );
  };

  const handleSaveSection = async (sectionNumber) => {
    setLoading(true);
    try {
      const section = sections.find(s => s.section_number === sectionNumber);
      await axios.put(
        `/cashier/ads-sections/${sectionNumber}`,
        {
          section_type: section.section_type,
          youtube_url: section.youtube_url || null,
          logo_url: section.logo_url || null,
          title: section.title,
          description: section.description,
          is_active: section.is_active ? 1 : 0
        },
        getHeaders()
      );
      toast.success(`Section ${sectionNumber} updated successfully!`);
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error(`Failed to save Section ${sectionNumber}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanyLogo = async () => {
    setLoading(true);
    try {
      await axios.put(
        `/cashier/company-logo`,
        { logo_url: companyLogo },
        getHeaders()
      );
      toast.success('Company logo updated successfully!');
    } catch (error) {
      console.error('Error saving company logo:', error);
      toast.error('Failed to save company logo');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      // Save all sections
      await Promise.all(
        sections.map(section =>
          axios.put(
            `/cashier/ads-sections/${section.section_number}`,
            {
              section_type: section.section_type,
              youtube_url: section.youtube_url || null,
              logo_url: section.logo_url || null,
              title: section.title,
              description: section.description,
              is_active: section.is_active ? 1 : 0
            },
            { headers: getHeaders() }
          )
        )
      );

      // Save company logo
      await axios.put(
        `/cashier/company-logo`,
        { logo_url: companyLogo },
        { headers: getHeaders() }
      );

      toast.success('All settings saved successfully!');
    } catch (error) {
      console.error('Error saving all settings:', error);
      toast.error('Failed to save some settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Header />
      <div className="ads-management-container">
        <ToastContainer />
        
        <div className="ads-management-header">
          <h1>🎬 Customer Display Ads Management</h1>
          <p>Configure the 3 horizontal advertisement sections for customer display</p>
        </div>

        <div className="ads-preview-section">
          <h2>📺 Preview Layout</h2>
          <div className="preview-layout">
            <div className="preview-section preview-section-1">
              <span>Section 1 (Left)</span>
              <small>{sections[0]?.section_type === 'video' ? '🎥 Video' : '🖼️ Image'}</small>
            </div>
            <div className="preview-section preview-section-2">
              <span>Section 2 (Center)</span>
              <small>🏢 Logo</small>
            </div>
            <div className="preview-section preview-section-3">
              <span>Section 3 (Right)</span>
              <small>{sections[2]?.section_type === 'video' ? '🎥 Video' : '🖼️ Image'}</small>
            </div>
          </div>
        </div>

        {/* Section 1 - Left Video */}
        <div className="ads-section-card">
          <div className="section-header">
            <h2>📹 Section 1 - Left Video</h2>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={sections[0]?.is_active || false}
                onChange={(e) => handleSectionChange(1, 'is_active', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={sections[0]?.title || ''}
              onChange={(e) => handleSectionChange(1, 'title', e.target.value)}
              placeholder="Section title"
            />
          </div>

          <div className="form-group">
            <label>YouTube Video URL</label>
            <input
              type="text"
              value={sections[0]?.youtube_url || ''}
              onChange={(e) => handleSectionChange(1, 'youtube_url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <small className="help-text">Paste the full YouTube video URL</small>
          </div>

          {sections[0]?.youtube_url && extractYouTubeVideoId(sections[0].youtube_url) && (
            <div className="video-preview">
              <h4>Preview:</h4>
              <iframe
                width="100%"
                height="200"
                src={`https://www.youtube.com/embed/${extractYouTubeVideoId(sections[0].youtube_url)}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Section 1 Preview"
              ></iframe>
            </div>
          )}

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={sections[0]?.description || ''}
              onChange={(e) => handleSectionChange(1, 'description', e.target.value)}
              placeholder="Description for this section"
              rows="3"
            />
          </div>

          <button 
            className="btn-save-section" 
            onClick={() => handleSaveSection(1)}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Section 1'}
          </button>
        </div>

        {/* Section 2 - Center Logo */}
        <div className="ads-section-card">
          <div className="section-header">
            <h2>🏢 Section 2 - Center Logo</h2>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={sections[1]?.is_active || false}
                onChange={(e) => handleSectionChange(2, 'is_active', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label>Company Logo URL</label>
            <input
              type="text"
              value={companyLogo}
              onChange={(e) => setCompanyLogo(e.target.value)}
              placeholder="https://example.com/logo.png"
            />
            <small className="help-text">Enter the direct URL to your company logo image</small>
          </div>

          {companyLogo && (
            <div className="logo-preview">
              <h4>Preview:</h4>
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                style={{ maxWidth: '300px', maxHeight: '200px' }}
                onError={(e) => {
                  e.target.src = '';
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <button 
            className="btn-save-section" 
            onClick={handleSaveCompanyLogo}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Logo'}
          </button>
        </div>

        {/* Section 3 - Right Video */}
        <div className="ads-section-card">
          <div className="section-header">
            <h2>📹 Section 3 - Right Video</h2>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={sections[2]?.is_active || false}
                onChange={(e) => handleSectionChange(3, 'is_active', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={sections[2]?.title || ''}
              onChange={(e) => handleSectionChange(3, 'title', e.target.value)}
              placeholder="Section title"
            />
          </div>

          <div className="form-group">
            <label>YouTube Video URL</label>
            <input
              type="text"
              value={sections[2]?.youtube_url || ''}
              onChange={(e) => handleSectionChange(3, 'youtube_url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <small className="help-text">Paste the full YouTube video URL</small>
          </div>

          {sections[2]?.youtube_url && extractYouTubeVideoId(sections[2].youtube_url) && (
            <div className="video-preview">
              <h4>Preview:</h4>
              <iframe
                width="100%"
                height="200"
                src={`https://www.youtube.com/embed/${extractYouTubeVideoId(sections[2].youtube_url)}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Section 3 Preview"
              ></iframe>
            </div>
          )}

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={sections[2]?.description || ''}
              onChange={(e) => handleSectionChange(3, 'description', e.target.value)}
              placeholder="Description for this section"
              rows="3"
            />
          </div>

          <button 
            className="btn-save-section" 
            onClick={() => handleSaveSection(3)}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Section 3'}
          </button>
        </div>

        {/* Save All Button */}
        <div className="save-all-section">
          <button 
            className="btn-save-all" 
            onClick={handleSaveAll}
            disabled={loading}
          >
            {loading ? 'Saving All...' : '💾 Save All Settings'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default AdsManagement;
